import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, FileText, CheckCircle2, AlertTriangle, Loader2, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { stageClasses, fmtDate, isOverdue } from "@/lib/pipeline";
import Seo from "@/components/Seo";

function StatCard({ icon: Icon, label, value, tone = "navy", testId }) {
  const toneCls = tone === "gold" ? "text-ei-golddark" : tone === "red" ? "text-red-700" : "text-ei-navy";
  return (
    <div className="bg-white border border-ei-mist rounded-md p-5 sm:p-6" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="ei-eyebrow">{label}</span>
        <Icon className={`w-5 h-5 ${toneCls}`} strokeWidth={1.5} />
      </div>
      <div className={`mt-3 font-display text-3xl font-bold tnum ${toneCls}`}>{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [s, r] = await Promise.all([api.get("/enquiries/stats"), api.get("/enquiries")]);
        if (!active) return;
        setStats(s.data);
        setRecent(r.data.slice(0, 6));
      } catch (e) {
        setError("Could not load dashboard data.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-ei-navy" /></div>;
  }
  if (error) {
    return <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-6" data-testid="dashboard-error">{error}</div>;
  }

  return (
    <div>
      <Seo title="Dashboard — EI Battery Workspace" />
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <span className="ei-eyebrow">Overview</span>
          <h1 className="mt-1 text-2xl sm:text-3xl font-bold text-ei-navy">Welcome, {user?.name?.split(" ")[0]}</h1>
        </div>
        <Link to="/staff/enquiries" className="font-display text-sm font-bold text-ei-navy hover:text-ei-golddark inline-flex items-center gap-2" data-testid="dashboard-view-all">
          View all enquiries <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Inbox} label="Total enquiries" value={stats.total} testId="stat-total" />
        <StatCard icon={FileText} label="Open" value={stats.open} testId="stat-open" />
        <StatCard icon={FileText} label="In quoting" value={stats.in_quoting} tone="gold" testId="stat-quoting" />
        <StatCard icon={AlertTriangle} label="Overdue follow-ups" value={stats.overdue_followups} tone={stats.overdue_followups > 0 ? "red" : "navy"} testId="stat-overdue" />
      </div>

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        {/* Recent enquiries */}
        <div className="lg:col-span-2 bg-white border border-ei-mist rounded-md">
          <div className="px-5 py-4 border-b border-ei-mist"><h2 className="font-display font-semibold text-ei-navy">Recent enquiries</h2></div>
          {recent.length === 0 ? (
            <div className="p-10 text-center text-ei-graphite/60" data-testid="dashboard-empty">
              <Inbox className="w-10 h-10 mx-auto text-ei-mist" />
              <p className="mt-3 text-sm">No enquiries yet. New website enquiries will appear here.</p>
            </div>
          ) : (
            <ul className="divide-y divide-ei-mist">
              {recent.map((e) => (
                <li key={e.id}>
                  <Link to={`/staff/enquiries/${e.id}`} className="flex items-center justify-between gap-3 px-5 py-4 hover:bg-ei-white transition-colors" data-testid={`dashboard-enquiry-${e.id}`}>
                    <div className="min-w-0">
                      <div className="font-semibold text-ei-navy truncate">{e.business_name}</div>
                      <div className="text-xs text-ei-graphite/60 mt-0.5 tnum">{e.reference} · {fmtDate(e.created_at)}</div>
                    </div>
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-sm border whitespace-nowrap ${stageClasses(e.stage)}`}>{e.stage}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Pipeline snapshot */}
        <div className="bg-white border border-ei-mist rounded-md">
          <div className="px-5 py-4 border-b border-ei-mist"><h2 className="font-display font-semibold text-ei-navy">Pipeline</h2></div>
          <ul className="p-3">
            {Object.entries(stats.by_stage).filter(([, v]) => v > 0).length === 0 && (
              <li className="px-2 py-6 text-center text-sm text-ei-graphite/60">No active pipeline yet.</li>
            )}
            {Object.entries(stats.by_stage).map(([stage, count]) => (
              count > 0 ? (
                <li key={stage} className="flex items-center justify-between px-2 py-2">
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-sm border ${stageClasses(stage)}`}>{stage}</span>
                  <span className="font-display font-bold text-ei-navy tnum">{count}</span>
                </li>
              ) : null
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
